export interface FaturaInadimplente {
  id: string;
  cliente_nome: string;
  cliente_cpf_cnpj?: string | null;
  uc_codigo: string;
  /** Troca de dono da UC: a fatura é do dono ANTERIOR, não de quem está hoje no
   *  cadastro da UC. Fica fora da rota de cobrança e não herda contato/endereço da UC. */
  dono_anterior_troca_id?: string | null;
  valor_boleto_vigor: number;
  /** Boleto único/parceira: é aqui que fica o valor cobrado (valor_boleto_vigor = 0). */
  valor_emissao_boleto_unico?: number | null;
  vencimento_boleto: string;
  data_vencimento: string;
  status_boleto_vigor: string;
  valor_parcelamentos_boleto: number;
  parcelamento_descricao: string | null;
  saldo_acumulado_kwh: number;
  mes_referencia: string;
  status_fatura: string;
  motivo_cancelamento_boleto: string | null;
  end_cidade?: string;
  // Usados por derivarStatusBoleto (src/lib/statusBoleto.ts) para decidir se a
  // fatura é mesmo inadimplência — o campo status_boleto_vigor sozinho mente.
  data_pgto_boleto?: string | null;
  quitado_em?: string | null;
  cancelado_em?: string | null;
  // Extras derived via JOIN or heuristics
  consultor_responsavel?: string;
  titularidade?: string;
  // Situação do cadastro da UC ('Ativa', 'Cancelada', 'Pausada'...), resolvida
  // pelo hook a partir de unidades_consumidoras. 'Cancelada' é o "rateio já
  // finalizado" do chamado de débitos antigos: o associado saiu, a dívida ficou.
  situacao_uc?: string | null;

  // ---- Cobrança em campo (rota de visita ao inadimplente) -------------------
  // Endereço do pagador impresso no boleto, extraído da fatura da Equatorial.
  // Só serve de resgate: o número da casa falta em 71% do cadastro da UC e a
  // fatura o tem em 616 casos — é essa união que torna o geocode viável.
  end_logradouro?: string | null;
  end_numero?: string | null;
  end_bairro?: string | null;
  end_cep?: string | null;
  end_uf?: string | null;
  /** PDF da fatura da distribuidora. Público, e o link com melhor cobertura
   *  entre os vencidos: 2.455 de 2.999 têm (medido em 10/09/2026). */
  pdf_fatura?: string | null;
  // Meios de pagamento do boleto da Vigor. Nenhum deles é garantido: só 1.350
  // das 2.999 faturas vencidas têm ALGUM (45%), e o link público (token) existe
  // em apenas 11. Por isso a tela mostra o que houver em vez de prometer PDF.
  url_boleto?: string | null;
  url_boleto_combinado?: string | null;
  pdf_boleto_combinado?: string | null;
  token_boleto_publico?: string | null;
  linha_digitavel_boleto?: string | null;
  codigo_pix_boleto?: string | null;
  /** Sem ele o aviso de boleto Sicoob quebra no parcelamento — é o campo que
   *  CriarParcelamentoModal exige no objeto pré-selecionado. */
  num_cobranca_boleto?: string | null;
}

/** Cadastro da UC lido junto com a inadimplência, para a rota de cobrança. */
export interface CadastroUc {
  id: number | null;
  cpf: string;
  situacao: string | null;
  titular_fatura?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  cep?: string | null;
  endereco?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  geocode_status?: string | null;
  /** Endereço canônico confirmado pelo geocoder — destino do link de navegação. */
  geocode_label?: string | null;
  whatsapp_cliente?: string | null;
  whatsapp_cliente_2?: string | null;
}

/**
 * Uma parada em potencial da rota de cobrança.
 *
 * A Central consolida a dívida por NOME DE CLIENTE, que é o recorte certo para
 * negociar. Para ir até lá cobrar, o recorte tem que ser a UC: o endereço é dela,
 * e um cliente pode ter UCs em cidades diferentes — SUPER GELO tem 4.
 */
export interface UcInadimplente {
  /** Chave normalizada da UC (as duas numerações da Equatorial resolvem na mesma). */
  chave: string;
  ucId: number | null;
  ucCodigo: string;
  /** Quem assina a fatura na Equatorial. Pode diferir do associado da Vigor. */
  titular: string | null;
  clienteNome: string;
  cpfCnpj: string | null;
  telefones: string[];
  enderecoTexto: string;
  cidade: string;
  bairro: string | null;
  latitude: number | null;
  longitude: number | null;
  geocodeStatus: string | null;
  /** A coordenada serve para ORDENAR a rota? Só o nível 'endereco' serve. */
  coordenadaConfiavel: boolean;
  situacaoUc: string | null;
  qtdFaturas: number;
  valorTotal: number;
  vencMaisAntigo: string | null;
  diasAtrasoMax: number;
  /** Fatura mais recente com PDF da distribuidora — o link que vai na mensagem. */
  pdfFatura: string | null;
  faturas: FaturaInadimplente[];
  cadastro: CadastroUc | null;
}

export type ClassificacaoRisco = 'RISCO ESTRATÉGICO ALTO' | 'RISCO ALTO' | 'RISCO MÉDIO' | 'RISCO BAIXO';
export type ScorePagamento = 'A' | 'B' | 'C' | 'D' | 'E';

export interface ClienteConsolidado {
  nome: string;
  // CPF/CNPJ representativo (primeiro não vazio encontrado entre as faturas
  // consolidadas deste nome) — chave usada por cobranca_clientes para abrir
  // o modal de gerenciamento de cobrança.
  cpfCnpj?: string | null;
  valorTotal: number;
  qtdFaturas: number;
  saldoAcumulado: number;
  ucs: string[];
  competencias: string[];
  cidades: string[];
  vencMaisAntigo: string | null;
  pct: number;
  pctAcum: number;
  curvaAbc: 'A' | 'B' | 'C';
  risco: ClassificacaoRisco;
  score: ScorePagamento;
  interno: boolean;
  consultor_responsavel?: string;
  titularidade?: string;
  /** Situações distintas das UCs deste cliente, para exibição. */
  situacoesUc: string[];
  /** Todas as UCs conhecidas deste cliente estão canceladas — rateio encerrado
   *  com saldo devedor em aberto, o caso central do chamado de +180 dias. */
  rateioFinalizado: boolean;
  /** encerrado / sem rateio / ativo / não informada — ver lib/situacaoRateio.ts (#6166). */
  situacaoRateio: import('./lib/situacaoRateio').SituacaoRateioCliente;
  /** Dias de atraso da fatura mais antiga em aberto, contados do vencimento. */
  diasAtrasoMax: number;
  /** Idade real da dívida: o maior entre o atraso do boleto e o tempo decorrido
   *  desde a competência. Fatura migrada do Power Rev tem vencimento
   *  recarimbado e mente pelo primeiro critério — ver diasDesdeCompetencia. */
  diasDividaMax: number;
  /** Competência mais antiga em aberto, em 'yyyy-mm'. */
  competenciaMaisAntiga: string | null;
  faturas: FaturaInadimplente[];
}

export interface FiltrosGlobais {
  cidade: string;
  consultor: string;
  statusFinanceiro: string;
  faixaDebito: string;
  faixaAtraso: string;
  competencia: string;
  ano: string;
  mes: string;
  score: string;
  risco: string;
  saldoAcumulado: string;
  titularidade: string;
  busca: string;
  // Recorte de período: por vencimento (dataInicio/Fim = yyyy-mm-dd) ou por
  // competência/mês da fatura (dataInicio/Fim = yyyy-mm). Vazio = sem recorte.
  periodoModo: 'vencimento' | 'competencia';
  dataInicio: string;
  dataFim: string;
}

export const initialFiltros: FiltrosGlobais = {
  cidade: 'Todas',
  consultor: 'Todos',
  statusFinanceiro: 'Todos',
  faixaDebito: 'Todas',
  faixaAtraso: 'Todas',
  competencia: 'Todas',
  ano: 'Todos',
  mes: 'Todos',
  score: 'Todos',
  risco: 'Todos',
  saldoAcumulado: 'Todos',
  titularidade: 'Todas',
  busca: '',
  periodoModo: 'vencimento',
  dataInicio: '',
  dataFim: ''
};
